import type {
  BasemapColourGroup,
  BasemapPresetId,
  BasemapSemanticGroup,
  CuratedBasemapPresetId,
} from '../../basemaps/catalog';
import type { BasemapPresentationId } from '../../basemaps/presentation';
import type { BasemapLabelPresentationId } from '../../basemaps/labelPresentation';
import type { BasemapLabelAppearance } from '../../basemaps/labelAppearance';
import {
  COMPOSITION_GRID_SPACING_MM,
  type CompositionCamera,
  type CompositionLegend,
  type CompositionLogo,
  type CompositionInsetMap,
  type CompositionMapFrame,
  type CompositionNorthArrow,
  type CompositionPage,
  type CompositionScaleBar,
  type CompositionSourcesAttribution,
  type CompositionTextElement,
  type NormalizedRect,
} from '../../composition/model';
import { LEGACY_COMPOSITION_PIXELS_PER_MM } from '../../composition/scale';
import type { ExtentViewOptions } from '../../map/viewForExtent';
import type { ImportedVectorData } from '../../data/import/model';
import type { GeoPackageImportedItems } from '../../data/import/geopackageCore';
import type { ReplacementFieldResolutions } from '../../data/replacement';
import type { ImportedTableData } from '../../data/import/tabularImport';
import type { SourceId } from '../../data/model';
import type { VectorFeature } from '../../data/model';
import type { AreaOutput } from '../../selection/createArea';
import type { LayerLabelPatch } from '../../labels/validation';
import type { Entitlement, ProductEdition } from '../../licensing/entitlements';
import type { MapView } from '../../map/model';
import type { SupportedGeometry } from '../../data/model';
import type { InspectedFeature } from '../../popups/inspection';
import type { ProjectModelDraft, LayerId } from '../../project/model';
import type { AttributeRuleGroup } from '../../rules/model';
import type {
  MapSelectionMutation,
  SelectedFeatureRef,
  SelectionMethod,
  SelectionMutation,
} from '../../selection/model';
import type {
  GeometrySymbolPatch,
  GraduatedGeneratedMethod,
  GraduatedVisualVariable,
  StyleClassificationMode,
} from '../../styles/model';
import type { PaletteId } from '../../styles/palettes';
import type { JoinDraft, JoinId } from '../../joins/model';
import type {
  Annotation,
  AnnotationCoordinate,
  AnnotationCreationTool,
  AnnotationId,
  AnnotationLayerId,
  AnnotationMovePart,
} from '../../annotations/model';
import type { ApplicationMode, OpenInputKind } from '../modes/applicationModes';

export type InspectorTab = 'style' | 'labels' | 'popups' | 'data';
export type CompositionElementSelection =
  | 'legend'
  | 'logo'
  | 'inset-map'
  | 'map-frame'
  | 'north-arrow'
  | 'scale-bar'
  | 'sources-attribution'
  | 'subtitle'
  | 'title'
  | null;
export type CompositionInspectorSection =
  | 'page'
  | 'map-frame'
  | 'title'
  | 'subtitle'
  | 'legend'
  | 'logo'
  | 'inset-map'
  | 'scale-bar'
  | 'north-arrow'
  | 'sources-attribution'
  | 'guides';

export interface ShellUiState {
  /** Navigation and overlay from Search; never part of the project document. */
  readonly searchTarget: {
    readonly label: string;
    readonly view: MapView;
    readonly geometry: SupportedGeometry;
  } | null;
  readonly selectedLayerId: LayerId | null;
  readonly selectedTableSourceId: SourceId | null;
  readonly expandedLayerIds: readonly LayerId[];
  readonly activeInspectorTab: InspectorTab;
  readonly attributeTableOpen: boolean;
  readonly layersPanelCollapsed: boolean;
  readonly inspectorOpen: boolean;
  readonly inspectedFeature: InspectedFeature | null;
  readonly selectionToolActive: boolean;
  readonly selectionMethod: SelectionMethod | null;
  readonly selectedFeatures: readonly SelectedFeatureRef[];
  readonly selectedCompositionElement: CompositionElementSelection;
  readonly compositionInspectorSection: CompositionInspectorSection | null;
  readonly compositionGridVisible: boolean;
  readonly compositionGridSpacingMm: number;
  readonly compositionSnapEnabled: boolean;
  readonly compositionObjectSnapEnabled: boolean;
  readonly compositionPreviewPixelsPerMm: number;
  /** Ephemeral target and edit state; never serialized into `.placeio`. */
  readonly activeAnnotationLayerId: AnnotationLayerId | null;
  readonly selectedAnnotationId: AnnotationId | null;
  readonly annotationCreationTool: AnnotationCreationTool | null;
  readonly annotationDraftCoordinates: readonly AnnotationCoordinate[];
  readonly replacingFocusAreaId: AnnotationId | null;
}

export const DEFAULT_SHELL_UI_STATE: ShellUiState = {
  searchTarget: null,
  selectedLayerId: null,
  selectedTableSourceId: null,
  expandedLayerIds: [],
  activeInspectorTab: 'data',
  attributeTableOpen: false,
  layersPanelCollapsed: false,
  inspectorOpen: false,
  inspectedFeature: null,
  selectionToolActive: false,
  selectionMethod: null,
  selectedFeatures: [],
  selectedCompositionElement: null,
  compositionInspectorSection: 'page',
  compositionGridVisible: false,
  compositionGridSpacingMm: COMPOSITION_GRID_SPACING_MM,
  compositionSnapEnabled: false,
  compositionObjectSnapEnabled: true,
  compositionPreviewPixelsPerMm: LEGACY_COMPOSITION_PIXELS_PER_MM,
  activeAnnotationLayerId: null,
  selectedAnnotationId: null,
  annotationCreationTool: null,
  annotationDraftCoordinates: [],
  replacingFocusAreaId: null,
};

export type ActionResult = { readonly ok: true } | { readonly ok: false; readonly message: string };
export type StyleChangeResult = ActionResult;
export type LabelChangeResult = ActionResult;
export type PopupChangeResult = ActionResult;
export type DisplayFilterChangeResult = ActionResult;
export type DataChangeResult = ActionResult;

export interface JoinRemovalImpact {
  readonly joinName: string;
}

export type JoinRemovalInspectionResult =
  | { readonly ok: true; readonly impact: JoinRemovalImpact }
  | { readonly ok: false; readonly message: string };

export interface AppState {
  readonly mode: ApplicationMode;
  readonly entitlement: Entitlement;
  readonly project: ProjectModelDraft;
  readonly shell: ShellUiState;
  readonly symbolImportBusy: boolean;
  readonly symbolImportMessage: string;
  readonly importPointSymbol: (
    layerId: string,
    file: File,
    replaceId?: string,
    confirmedUses?: number,
  ) => Promise<StyleChangeResult>;
  readonly importSymbolForTarget: (
    target: import('../../symbols/targets').SymbolTarget,
    file: File,
    replaceId?: string,
    confirmedUses?: number,
  ) => Promise<StyleChangeResult>;
  readonly cancelSymbolImport: () => void;
  readonly renameSymbolAsset: (id: string, name: string) => StyleChangeResult;
  readonly removeSymbolAsset: (id: string) => StyleChangeResult;
  readonly transitionTo: (mode: ApplicationMode) => void;
  readonly openBlankMap: () => void;
  readonly showSearchTarget: (target: NonNullable<ShellUiState['searchTarget']>) => void;
  readonly clearSearchNavigation: () => void;
  readonly openInput: (input: OpenInputKind) => void;
  readonly addImportedVector: (imported: ImportedVectorData) => void;
  readonly addImportedVectors: (imported: readonly ImportedVectorData[]) => void;
  readonly addImportedTable: (imported: ImportedTableData) => void;
  readonly addImportedGeoPackage: (imported: GeoPackageImportedItems) => void;
  readonly closeAttributeTable: () => void;
  readonly closeInspector: () => void;
  readonly closeFeatureInspection: () => void;
  readonly fitToData: (viewport?: ExtentViewOptions) => void;
  readonly newMap: () => void;
  readonly openAttributeTable: () => void;
  readonly replaceProject: (project: ProjectModelDraft) => void;
  readonly replaceLayerData: (
    layerId: LayerId,
    imported: ImportedVectorData,
    resolutions: ReplacementFieldResolutions,
  ) => DataChangeResult;
  readonly removeLayer: (layerId: LayerId) => void;
  readonly renameLayer: (layerId: LayerId, name: string) => StyleChangeResult;
  readonly removeTableSource: (sourceId: SourceId) => DataChangeResult;
  readonly reorderLayer: (layerId: LayerId, targetIndex: number) => void;
  readonly selectLayer: (layerId: LayerId) => void;
  readonly inspectFeature: (feature: InspectedFeature | null) => void;
  readonly setSelectionToolActive: (active: boolean) => void;
  readonly setSelectionMethod: (method: SelectionMethod | null) => void;
  readonly selectFeatureFromMap: (
    feature: SelectedFeatureRef | null,
    mutation: MapSelectionMutation,
  ) => void;
  readonly selectFeaturesFromMap: (
    features: readonly SelectedFeatureRef[],
    mutation: MapSelectionMutation,
  ) => void;
  readonly selectFeaturesByAttribute: (
    layerId: LayerId,
    query: AttributeRuleGroup,
    mutation: Exclude<SelectionMutation, 'toggle'>,
  ) => DataChangeResult & { readonly matchCount?: number };
  readonly toggleFeatureSelection: (feature: SelectedFeatureRef) => void;
  readonly clearSelection: () => void;
  readonly clearLayerSelection: (layerId: LayerId) => void;
  readonly setActiveInspectorTab: (tab: InspectorTab) => void;
  readonly toggleLayerVisibility: (layerId: LayerId) => void;
  readonly toggleLayerExpanded: (layerId: LayerId) => void;
  readonly toggleLayersPanel: () => void;
  readonly viewLayerData: (layerId: LayerId) => void;
  readonly viewTableData: (sourceId: SourceId) => void;
  readonly addLayerJoin: (layerId: LayerId, draft: JoinDraft) => DataChangeResult;
  readonly updateLayerJoin: (
    layerId: LayerId,
    joinId: JoinId,
    draft: JoinDraft,
  ) => DataChangeResult;
  readonly inspectLayerJoinRemoval: (
    layerId: LayerId,
    joinId: JoinId,
  ) => JoinRemovalInspectionResult;
  readonly removeLayerJoin: (layerId: LayerId, joinId: JoinId) => DataChangeResult;
  readonly zoomToLayer: (layerId: LayerId, viewport?: ExtentViewOptions) => void;
  readonly setMockEdition: (edition: ProductEdition) => void;
  readonly setBasemapPreset: (presetId: BasemapPresetId) => StyleChangeResult;
  readonly applyBasemapPresentation: (presentation: BasemapPresentationId) => StyleChangeResult;
  readonly applyBasemapLabelPresentation: (
    presentation: BasemapLabelPresentationId,
  ) => StyleChangeResult;
  readonly setBasemapBuildings3d: (enabled: boolean) => StyleChangeResult;
  readonly setBasemapContextOpacity: (opacity: number) => StyleChangeResult;
  readonly setBasemapLabelsOpacity: (opacity: number) => StyleChangeResult;
  readonly setBasemapLabelAppearance: (patch: Partial<BasemapLabelAppearance>) => StyleChangeResult;
  readonly resetBasemapLabelAppearance: () => StyleChangeResult;
  readonly setBasemapVisibility: (
    group: BasemapSemanticGroup,
    visible: boolean,
  ) => StyleChangeResult;
  readonly setBasemapColour: (
    group: BasemapColourGroup,
    colour: string | null,
  ) => StyleChangeResult;
  readonly resetBasemapCustomization: (presetId?: CuratedBasemapPresetId) => StyleChangeResult;
  readonly setMapBackgroundColour: (colour: string) => StyleChangeResult;
  readonly updateCompositionPage: (patch: Partial<CompositionPage>) => StyleChangeResult;
  readonly applyCompositionTemplate: (
    template: import('../../composition/templates').CompositionTemplateId,
  ) => StyleChangeResult;
  readonly updateCompositionMapFrameRadius: (cornerRadiusMm: number) => StyleChangeResult;
  readonly updateCompositionMapFrameBounds: (bounds: NormalizedRect) => StyleChangeResult;
  readonly updateCompositionMapFrameBorder: (
    patch: Partial<CompositionMapFrame['border']>,
  ) => StyleChangeResult;
  readonly updateCompositionCamera: (camera: CompositionCamera) => StyleChangeResult;
  readonly useEditorViewForComposition: () => StyleChangeResult;
  readonly updateCompositionText: (
    element: 'subtitle' | 'title',
    patch: Partial<CompositionTextElement>,
  ) => StyleChangeResult;
  readonly updateCompositionLegend: (patch: Partial<CompositionLegend>) => StyleChangeResult;
  readonly updateCompositionInsetMap: (inset: CompositionInsetMap | null) => StyleChangeResult;
  readonly updateCompositionLogo: (logo: CompositionLogo | null) => StyleChangeResult;
  readonly updateCompositionScaleBar: (patch: Partial<CompositionScaleBar>) => StyleChangeResult;
  readonly updateCompositionNorthArrow: (
    patch: Partial<CompositionNorthArrow>,
  ) => StyleChangeResult;
  readonly updateCompositionSourcesAttribution: (
    patch: Partial<CompositionSourcesAttribution>,
  ) => StyleChangeResult;
  readonly selectCompositionElement: (element: CompositionElementSelection) => void;
  readonly setCompositionInspectorSection: (section: CompositionInspectorSection | null) => void;
  readonly setCompositionGridVisible: (visible: boolean) => void;
  readonly setCompositionGridSpacingMm: (spacingMm: number) => void;
  readonly setCompositionSnapEnabled: (enabled: boolean) => void;
  readonly setCompositionObjectSnapEnabled: (enabled: boolean) => void;
  readonly setCompositionPreviewPixelsPerMm: (pixelsPerMm: number) => void;
  readonly setLayerLabelsEnabled: (layerId: LayerId, enabled: boolean) => LabelChangeResult;
  readonly setLayerLabelFilter: (
    layerId: LayerId,
    filter: AttributeRuleGroup | null,
  ) => LabelChangeResult;
  readonly setLayerDisplayFilter: (
    layerId: LayerId,
    filter: AttributeRuleGroup | null,
  ) => DisplayFilterChangeResult;
  readonly updateLayerLabels: (layerId: LayerId, patch: LayerLabelPatch) => LabelChangeResult;
  readonly setLayerPopupsEnabled: (layerId: LayerId, enabled: boolean) => PopupChangeResult;
  readonly setLayerPopupTitleField: (layerId: LayerId, field: string | null) => PopupChangeResult;
  readonly setLayerPopupFieldVisibility: (
    layerId: LayerId,
    field: string,
    visible: boolean,
  ) => PopupChangeResult;
  readonly setLayerPopupFieldsVisible: (layerId: LayerId, visible: boolean) => PopupChangeResult;
  readonly setLayerPopupFieldLabel: (
    layerId: LayerId,
    field: string,
    label: string,
  ) => PopupChangeResult;
  readonly moveLayerPopupField: (
    layerId: LayerId,
    field: string,
    direction: -1 | 1,
  ) => PopupChangeResult;
  readonly setLayerCategoryColour: (
    layerId: LayerId,
    category: number | 'other',
    colour: string,
  ) => StyleChangeResult;
  readonly setLayerClassColour: (
    layerId: LayerId,
    numericClass: number | 'null',
    colour: string,
  ) => StyleChangeResult;
  readonly setLayerClassificationField: (layerId: LayerId, fieldName: string) => StyleChangeResult;
  readonly setLayerClassificationMode: (
    layerId: LayerId,
    mode: StyleClassificationMode,
  ) => StyleChangeResult;
  readonly setLayerGraduatedClassCount: (layerId: LayerId, classCount: number) => StyleChangeResult;
  readonly setLayerGraduatedMethod: (
    layerId: LayerId,
    method: GraduatedGeneratedMethod,
  ) => StyleChangeResult;
  readonly setLayerGraduatedVisualVariable: (
    layerId: LayerId,
    visualVariable: GraduatedVisualVariable,
  ) => StyleChangeResult;
  readonly setLayerGraduatedBoundary: (
    layerId: LayerId,
    boundaryIndex: number,
    boundary: number,
  ) => StyleChangeResult;
  readonly setLayerGraduatedPaletteReversed: (
    layerId: LayerId,
    reversed: boolean,
  ) => StyleChangeResult;
  readonly resetLayerGraduatedBreaks: (layerId: LayerId) => StyleChangeResult;
  readonly setLayerPalette: (layerId: LayerId, paletteId: PaletteId) => StyleChangeResult;
  readonly updateLayerCategoryOverride: (
    layerId: LayerId,
    category: number | 'other',
    patch: GeometrySymbolPatch,
  ) => StyleChangeResult;
  readonly resetLayerCategoryOverride: (
    layerId: LayerId,
    category: number | 'other',
  ) => StyleChangeResult;
  readonly updateLayerClassOverride: (
    layerId: LayerId,
    numericClass: number | 'null',
    patch: GeometrySymbolPatch,
  ) => StyleChangeResult;
  readonly resetLayerClassOverride: (
    layerId: LayerId,
    numericClass: number | 'null',
  ) => StyleChangeResult;
  readonly setLayerThematicLegendLabel: (
    layerId: LayerId,
    entry: number | 'other' | 'null',
    label: string,
  ) => StyleChangeResult;
  readonly updateLayerSymbol: (layerId: LayerId, patch: GeometrySymbolPatch) => StyleChangeResult;
  readonly updateMapView: (view: MapView) => void;
  readonly addAnnotationLayer: () => DataChangeResult;
  readonly createAreaFromSelection: (
    features: readonly VectorFeature[],
    output: AreaOutput,
    bufferMetres: number,
  ) => DataChangeResult;
  readonly removeAnnotationLayer: (layerId: AnnotationLayerId) => DataChangeResult;
  readonly renameAnnotationLayer: (layerId: AnnotationLayerId, name: string) => DataChangeResult;
  readonly reorderAnnotationLayer: (
    layerId: AnnotationLayerId,
    targetIndex: number,
  ) => DataChangeResult;
  readonly selectAnnotationLayer: (layerId: AnnotationLayerId) => void;
  readonly toggleAnnotationLayerVisibility: (layerId: AnnotationLayerId) => void;
  readonly setAnnotationLayerLegend: (
    layerId: AnnotationLayerId,
    includeInLegend: boolean,
    legendLabel?: string,
  ) => DataChangeResult;
  readonly startAnnotationDrawing: (
    tool: AnnotationCreationTool,
    replacingFocusAreaId?: AnnotationId | null,
  ) => DataChangeResult;
  readonly placeAnnotationCoordinate: (
    coordinate: AnnotationCoordinate,
    finish?: boolean,
  ) => DataChangeResult;
  readonly finishAnnotationDrawing: () => DataChangeResult;
  readonly cancelAnnotationDrawing: () => void;
  readonly selectAnnotation: (layerId: AnnotationLayerId, annotationId: AnnotationId) => void;
  readonly updateAnnotation: (
    layerId: AnnotationLayerId,
    annotationId: AnnotationId,
    update: (annotation: Annotation) => Annotation,
  ) => DataChangeResult;
  readonly moveAnnotation: (
    layerId: AnnotationLayerId,
    annotationId: AnnotationId,
    longitudeDelta: number,
    latitudeDelta: number,
    part?: AnnotationMovePart,
    widthDelta?: number,
    heightDelta?: number,
  ) => DataChangeResult;
  readonly moveAnnotationToLayer: (
    sourceLayerId: AnnotationLayerId,
    annotationId: AnnotationId,
    targetLayerId: AnnotationLayerId,
  ) => DataChangeResult;
  readonly moveAnnotationToNewLayer: (
    sourceLayerId: AnnotationLayerId,
    annotationId: AnnotationId,
  ) => DataChangeResult;
  readonly removeAnnotation: (
    layerId: AnnotationLayerId,
    annotationId: AnnotationId,
  ) => DataChangeResult;
  readonly setActiveFocusArea: (annotationId: AnnotationId | null) => DataChangeResult;
}
